/**
 * Avatar — User avatar with image or initials fallback.
 */

import { clsx } from 'clsx';
import type { AuthUser } from '@/features/auth/auth.types';

export interface AvatarProps {
  user: AuthUser | null;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}

const sizeClasses = {
  xs: 'w-6 h-6 text-xs',
  sm: 'w-8 h-8 text-sm',
  md: 'w-10 h-10 text-base',
  lg: 'w-14 h-14 text-lg',
};

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function Avatar({ user, size = 'md', className }: AvatarProps) {
  const name = user?.displayName || user?.username || 'User';
  const avatarUrl = user?.avatarUrl;

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={`${name}'s avatar`}
        className={clsx('rounded-full object-cover', sizeClasses[size], className)}
      />
    );
  }

  return (
    <div
      className={clsx(
        'rounded-full bg-primary-600 text-white font-semibold flex items-center justify-center',
        sizeClasses[size],
        className,
      )}
      aria-label={`${name}'s avatar`}
    >
      {getInitials(name)}
    </div>
  );
}

export default Avatar;